const STORAGE_KEY = 'auth0_pending_return_to';

interface WindowWithPostLoginPath extends Window {
  __synapsisPostLoginPath?: string;
}

export function sanitizeAuthReturnPath(value: unknown): string {
  if (typeof value !== 'string' || value.length === 0) {
    return '/matters';
  }
  if (!value.startsWith('/') || value.startsWith('//')) {
    return '/matters';
  }
  if (value.includes('://')) {
    return '/matters';
  }
  return value;
}

/** Called from Auth0 `onRedirectCallback` — avoids `window.location.replace` (Safari / SPA state). */
export function storeAuthReturnPathFromCallback(
  appState: { returnTo?: string } | null | undefined,
): void {
  const path = sanitizeAuthReturnPath(appState?.returnTo ?? '/matters');
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch {
    // Private mode / quota
  }
}

/**
 * Resolves the path to open after login. Caches on `window` so React StrictMode's
 * double mount does not read sessionStorage twice (second read would be empty).
 */
export function resolvePostLoginNavigateTarget(): string {
  const win = window as unknown as WindowWithPostLoginPath;
  const cached = win.__synapsisPostLoginPath;
  if (cached !== undefined) {
    return cached;
  }
  let fromStorage = '/matters';
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (stored) {
      fromStorage = sanitizeAuthReturnPath(stored);
    }
  } catch {
    // ignore
  }
  win.__synapsisPostLoginPath = fromStorage;
  return fromStorage;
}

/** Call on logout so a later login can store a fresh return path. */
export function clearPostLoginNavigateState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  const win = window as unknown as WindowWithPostLoginPath;
  delete win.__synapsisPostLoginPath;
}
